import {
    Component,
    Input,
    OnInit,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    Output,
    EventEmitter,
} from '@angular/core';
import {
    FormGroup,
    FormControl,
    FormArray,
    Validators,
} from '@angular/forms';
import { of, Subject } from 'rxjs';
import {
    takeUntil,
    finalize,
    map,
    distinctUntilChanged,
    switchMap,
    tap,
    catchError,
} from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';

import { Contract, ContractFileGroup, UploadedFile, FileUpload } from '../../contracte-programari.interface';
import { BeneficiariService } from '../../../beneficiari/beneficiari.service';
import { DocumentCategoriesService } from 'src/app/platform/nomenclatoare/activities-docs-setari/document-categories/document-categories.service';
import { DocumentTagsService } from 'src/app/platform/nomenclatoare/activities-docs-setari/document-tags/document-tags.service';
import { NotificationService } from 'src/app/services/notification.service';
import { AppFilesService } from 'src/app/services/app-files.service';
import { DialogService } from 'src/app/services/dialog.service';
import { AddDocumentTagsComponent } from 'src/app/platform/nomenclatoare/activities-docs-setari/document-tags/add-document-tags/add-document-tags.component';
import { Generic } from 'src/app/interfaces/generic.interface';
import { ViewDocumentComponent } from './view-document/view-document.component';
import { helper } from 'src/environments/helper';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';
import { notDefaultValidator } from 'src/app/validators/not-default';
import {
    ContractDocCategoryIcons,
    OrderMode,
    ViewMode,
    DocumentTag,
    ViewCard,
} from './documente.model';

interface Tag { id: number; name: string; color: string; }

@Component({
    selector: 'app-documente',
    templateUrl: './documente.component.html',
    styleUrls: ['./documente.component.scss'],
})
export class DocumenteComponent implements OnInit, OnChanges, OnDestroy {
    @Input() contract!: Contract | null;
    @Output() contractChanged: EventEmitter<void> = new EventEmitter<void>();

    // ── ui state ──
    isLoading = false;
    showUploadPanel = false;
    uploading = false;

    editData: ContractFileGroup | null = null;
    uploadedFiles: UploadedFile[] = [];
    deletedFiles: UploadedFile[] = [];

    // ── lookups ──
    defaultParameter = { name: '--Alegeți--', id: -1 };

    documentCategories: (Generic & { slug: string })[] = [];
    documentTags: Tag[] = [];
    filteredDocumentTags: Tag[] = [];

    tagArr: Tag[] = [];
    activeCategory: string | null = null;

    fileGroups: ContractFileGroup[] = [];
    filteredFileGroups: ContractFileGroup[] = [];
    viewCards: ViewCard[] = [];

    // ── enums (template access) ──
    ContractDocCategoryIcons = ContractDocCategoryIcons;
    OrderMode = OrderMode;
    ViewMode = ViewMode;
    currentOrder: OrderMode = OrderMode.DESCENDING;
    currentView: ViewMode = ViewMode.BY_DATE;

    // ── forms ──
    documentTagsControl = new FormControl();

    form: FormGroup = new FormGroup({
        category_id: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
        tag_id: new FormControl(-1),
        document_nr: new FormControl(''),
        document_date: new FormControl(null),
        observations: new FormControl(''),
    });

    selectedCategorySlug: string = '';

    filesForm: FormGroup = new FormGroup({
        files: new FormArray([], [Validators.required]),
    });

    private destroy$ = new Subject<void>();

    // ── error / success labels ──
    errorTitle = helper.dialogConfig.headers.errorAtentie;
    errorIcon = helper.dialogConfig.icons.error;
    successTitle = helper.dialogConfig.headers.success;
    successIcon = helper.dialogConfig.icons.success;

    constructor(
        private dialog: MatDialog,
        private dialogService: DialogService,
        private beneficiariService: BeneficiariService,
        private documentCategoriesService: DocumentCategoriesService,
        private documentTagsService: DocumentTagsService,
        private notificationService: NotificationService,
        private appFilesService: AppFilesService,
        private toastr: ToastrService,
    ) { }

    // ── lifecycle ─────────────────────────────────────────────────────────────

    ngOnInit(): void {
        this.syncFileGroups();
        this.loadDocumentCategories();
        this.loadDocumentTags();
        this.renderFieldsSearch();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.syncFileGroups();
        this.filterByCategory(this.activeCategory);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ── helpers ───────────────────────────────────────────────────────────────

    private syncFileGroups(): void {
        this.fileGroups = this.contract?.file_groups ?? [];
        this.filteredFileGroups = [...this.fileGroups];
    }

    // ── load data ─────────────────────────────────────────────────────────────

    loadDocumentCategories(): void {
        this.isLoading = true;
        this.documentCategoriesService
            .getListActive()
            .pipe(
                // Filter only categories meant for contracts
                map((r: any) => (r.data as any[]).filter(c => c.show_in_contracts === true)),
                finalize(() => (this.isLoading = false)),
            )
            .subscribe({
                next: (r) => (this.documentCategories = r),
                error: (e) => console.error('Error loading document categories', e),
            });
    }

    loadDocumentTags(): void {
        this.isLoading = true;
        this.documentTagsService
            .getListActive()
            .pipe(
                map((r: any) => [...r.data]),
                finalize(() => (this.isLoading = false)),
            )
            .subscribe({
                next: (r) => {
                    this.documentTags = r;
                    this.filteredDocumentTags = [this.defaultParameter as any, ...r];
                    this.rebuildFilteredTags();
                },
                error: () => (this.filteredDocumentTags = []),
            });
    }

    // ── search / filter ───────────────────────────────────────────────────────

    private normalizeString(v: string): string {
        return v.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    /**
     * Rebuilds filteredDocumentTags from documentTags,
     * excluding tags already present in tagArr.
     */
    private rebuildFilteredTags(searchVal?: string): void {
        const available = this.documentTags.filter(
            t => !this.tagArr.some(a => a.id === t.id)
        );
        const withDefault = [this.defaultParameter as any, ...available];
        if (searchVal && searchVal.length >= 1) {
            const norm = this.normalizeString(searchVal);
            this.filteredDocumentTags = [
                this.defaultParameter as any,
                ...available.filter(t => this.normalizeString(t.name).includes(norm)),
            ];
        } else {
            this.filteredDocumentTags = withDefault;
        }
    }

    renderFieldsSearch(): void {
        this.documentTagsControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                distinctUntilChanged(),
                switchMap((val) => {
                    this.rebuildFilteredTags(val);
                    return of(this.filteredDocumentTags);
                }),
                tap((data) => (this.filteredDocumentTags = data)),
                catchError(() => of([])),
            )
            .subscribe();
    }

    onDocumentTagsClosed(): void {
        const isInList = this.filteredDocumentTags.some(
            (t) => t.id === this.form.get('tag_id')?.value,
        );
        if (!isInList) {
            this.documentTagsControl.setValue(null);
            this.rebuildFilteredTags();
            this.destroy$.next();
            this.destroy$.complete();
            this.renderFieldsSearch();
        }
    }

    // ── category / view ───────────────────────────────────────────────────────

    filterByCategory(slug: string | null): void {
        this.activeCategory = slug;
        this.filteredFileGroups = slug
            ? this.fileGroups.filter((g) => g.category_slug === slug)
            : [...this.fileGroups];
        this.sortFileGroups();
    }

    setOrder(mode: OrderMode): void {
        this.currentOrder = mode;
        this.sortFileGroups();
    }

    private sortFileGroups(): void {
        this.filteredFileGroups.sort(
            (a, b) =>
                this.currentOrder *
                (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()),
        );
        this.buildGroups();
    }

    setViewMode(mode: ViewMode): void {
        this.currentView = mode;
        this.buildGroups();
    }

    buildGroups(): void {
        switch (this.currentView) {
            case ViewMode.BY_CATEGORY: this.groupByCategory(); break;
            case ViewMode.BY_DATE: this.groupByDate(); break;
            case ViewMode.BY_TAG: this.groupByTag(); break;
        }
    }

    private groupByCategory(): void {
        const map = new Map<string, ContractFileGroup[]>();
        this.documentCategories.forEach((c) => map.set(c.slug, []));
        this.filteredFileGroups.forEach((g) => {
            const list = map.get(g.category_slug) ?? [];
            list.push(g);
            map.set(g.category_slug, list);
        });
        this.viewCards = this.documentCategories
            .filter((c) => (map.get(c.slug)?.length ?? 0) > 0)
            .map((c) => ({
                label: c.name,
                icon: ContractDocCategoryIcons[c.slug],
                fileGroups: map.get(c.slug)!,
            }));
    }

    private groupByDate(): void {
        const map = new Map<string, ContractFileGroup[]>();
        this.filteredFileGroups.forEach((g) => {
            const d = new Date(g.created_at);
            const key = `${d.getDate()} ${d.toLocaleString('ro-RO', { month: 'long' })} ${d.getFullYear()}`;
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(g);
        });
        this.viewCards = Array.from(map.entries()).map(([label, fileGroups]) => ({
            label,
            icon: 'fa-calendar',
            fileGroups,
        }));
    }

    private groupByTag(): void {
        const map = new Map<number, { name: string; color: string; docs: ContractFileGroup[] }>();
        this.filteredFileGroups.forEach((g) => {
            g.tags.forEach((tag) => {
                if (!map.has(tag.id)) map.set(tag.id, { name: tag.name, color: tag.color, docs: [] });
                map.get(tag.id)!.docs.push(g);
            });
        });
        this.viewCards = Array.from(map.values()).map((e) => ({
            label: e.name,
            color: e.color,
            fileGroups: e.docs,
        }));
    }

    // ── upload panel ──────────────────────────────────────────────────────────

    toggleUploadPanel(): void {
        this.showUploadPanel = !this.showUploadPanel;
        if (!this.showUploadPanel) this.resetUploadForm();
    }

    openEditPanel(group: ContractFileGroup): void {
        this.editData = group;
        this.showUploadPanel = true;

        this.selectedCategorySlug = group.category_slug ?? '';

        this.form.patchValue({
            category_id: group.category_id,
            tag_id: -1,
            document_nr: group.document_nr ?? '',
            document_date: group.document_date ?? null,
            observations: group.observations ?? '',
        });

        this.tagArr = group.tags ? [...group.tags] : [];
        this.rebuildFilteredTags();
        this.uploadedFiles = [...group.files];
    }

    onCategoryChange(catId: number): void {
        const cat = this.documentCategories.find(c => c.id === catId);
        this.selectedCategorySlug = cat?.slug ?? '';
    }

    private resetUploadForm(): void {
        this.form.patchValue({
            category_id: -1,
            tag_id: -1,
            document_nr: '',
            document_date: null,
            observations: '',
        });
        this.form.get('category_id')?.markAsUntouched();
        this.selectedCategorySlug = '';
        this.tagArr = [];
        (this.filesForm.get('files') as FormArray).clear();
        this.rebuildFilteredTags();
        if (this.editData) {
            this.editData = null;
            this.uploadedFiles = [];
            this.deletedFiles = [];
        }
    }

    // ── tags ──────────────────────────────────────────────────────────────────

    addTagInArray(): void {
        const id = this.form.controls['tag_id'].value as number;
        if (!id || id === -1) return;
        if (this.tagArr.find((t) => t.id === id)) return;

        const tag = this.documentTags.find((t) => t.id === id);
        if (tag) {
            this.tagArr.push({ ...tag });
            // Reset select back to default and remove the tag from the available list
            this.form.controls['tag_id'].setValue(-1);
            this.rebuildFilteredTags();
        }
    }

    removeTagInArray(i: number): void {
        this.tagArr.splice(i, 1);
        // Add the removed tag back into the available list
        this.rebuildFilteredTags();
    }

    addTagToNomenclator(): void {
        const ref = this.dialog.open(AddDocumentTagsComponent, {
            ...this.dialogService.getDialogConfig(),
        });
        ref.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.loadDocumentTags();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Înregistrare adăugată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    // ── files ─────────────────────────────────────────────────────────────────

    addFile(event: any): void {
        event.preventDefault();
        const input = event.target as HTMLInputElement;
        if (!input.files) return;

        for (let i = 0; i < input.files.length; i++) {
            const file = input.files[i];
            const formFiles: FileUpload[] = this.filesForm.get('files')?.value;
            if (formFiles.some((f) => f.original_name === file.name)) {
                this.openToast({ title: 'Atenție', message: `Fișierul "${file.name}" este deja încărcat.`, type: 'warning' });
                continue;
            }

            this.uploading = true;
            const fd = new FormData();
            fd.append('file', file, file.name);

            this.beneficiariService.fileUpload(fd).subscribe({
                next: (res: any) => {
                    const r = typeof res.status_code !== 'undefined' ? res : res.error;
                    if (r?.status_code === 200 && r.data) {
                        (this.filesForm.get('files') as FormArray).push(new FormControl(res.data));
                        this.openToast({ title: this.successTitle, message: helper.dialogConfig.generalMessages.fileSuccessfullyUploaded, type: this.successIcon });
                    } else {
                        this.notificationService.warningSwal(this.errorTitle, environment.config.customNotifications.generalMessages.error, this.errorIcon);
                    }
                    this.uploading = false;
                },
                error: (err: any) => {
                    this.uploading = false;
                    this.onHttpError(err.error);
                },
            });
        }
        input.value = '';
    }

    removeFile(i: number): void {
        const fa = this.filesForm.get('files') as FormArray;
        if (i > -1 && i < fa.length) fa.removeAt(i);
    }

    deleteFile(i: number): void {
        this.deletedFiles.push(this.uploadedFiles.splice(i, 1)[0]);
    }

    restoreFile(i: number): void {
        this.uploadedFiles.push(this.deletedFiles.splice(i, 1)[0]);
    }

    downloadTempFile(f: FileUpload): void {
        this.isLoading = true;
        this.appFilesService
            .postDownloadFile(this.beneficiariService.tempFileDownloadUrl, { fileName: f.filename, originalName: f.original_name })
            .then(() => (this.isLoading = false))
            .catch(() => (this.isLoading = false));
    }

    downloadFile(id: number): void {
        this.isLoading = true;
        this.appFilesService
            .downloadFile(this.beneficiariService.downloadFileUrl + id)
            .then(() => (this.isLoading = false))
            .catch(() => (this.isLoading = false));
    }

    // ── submit ────────────────────────────────────────────────────────────────

    submitUpload(): void {
        this.isLoading = true;

        const payload = {
            ...this.form.value,
            contract_id: this.contract?.id,
            category_slug: this.selectedCategorySlug,
            tag_id: this.tagArr.map((t) => t.id),
            files: this.filesForm.value.files,
            deleted_file_ids: this.editData
                ? this.deletedFiles.map((f) => f.id)
                : undefined,
        };

        const req$ = this.editData
            ? this.beneficiariService.updateFiles(this.contract!.entity_id, this.editData.id, payload)
            : this.beneficiariService.storeFiles(this.contract!.entity_id, payload);

        req$.pipe(finalize(() => (this.isLoading = false))).subscribe({
            next: () => {
                this.toggleUploadPanel();
                this.contractChanged.emit();
            },
            error: (err: any) => {
                let msg: string;
                try { msg = err.error.errors.message[0]; } catch { msg = helper.dialogConfig.generalMessages.error; }
                this.notificationService.warningSwal(this.errorTitle, msg, this.errorIcon);
            },
        });
    }

    // ── view document ─────────────────────────────────────────────────────────

    viewDocument(group: ContractFileGroup): void {
        this.dialog.open(ViewDocumentComponent, {
            width: '700px',
            maxWidth: '80vw',
            minHeight: '200px',
            maxHeight: '90vh',
            autoFocus: false,
            disableClose: true,
            data: { group },
        });
    }

    // ── toast helper ──────────────────────────────────────────────────────────

    onHttpError(res: any): void {
        const msg = res?.errors?.message?.[0] ?? environment.config.customNotifications.generalMessages.error;
        this.notificationService.warningSwal(this.errorTitle, msg, this.errorIcon);
    }

    openToast({ type, message, title }: { type: string; message: string; title: string }): void {
        const opts = { ...this.toastr.toastrConfig, progressBar: true, preventDuplicates: true, closeButton: true };
        this.toastr.show(message, title, opts, opts.iconClasses[type]);
    }
}